/* Build in libraries */

/* Third party libraries */

import ApprovedApplicationsWidget from '@/components/Rover/Widgets/ApprovedApplicationsWidget';
import AverageHouseholdIncome from '@/components/Rover/Widgets/AverageHouseholdIncome';
/* Services */
import TotalHousingPrice from '@/components/Rover/Widgets/TotalHousingPrice';
import Card from '@/components/UI/Card/index.tsx';
import DoughnutChart from '@/components/UI/Charts/DoughnutChart';
/* Components */
import PieChart from '@/components/UI/Charts/PieChart';
import {
  calcTotalHouseholdIncome,
  convertIntoValueAndPercentage,
  getAverageHouseholdIncome,
} from '@/features/ApplicationForm/FormConfig/helpers';
import { races } from '../BDPAForm/ApplicationInformation/constants';

interface IApplicationDashboardComponentProps {
  data: any;
}

interface IDoughnutChart {
  dataLabels: string[];
  dataSets: { data: number; color: string }[];
}

interface IPieChart {
  label: string[];
  dataSets: { data: number; color: string }[];
}

export const BDPAApprovedDashboard = ({ data }: IApplicationDashboardComponentProps) => {
  const applicationsList = data?.data;
  const widgetsData: {
    totalApplications: number;
    statusSummary: { [key: string]: number };
    totalCityEmployees: IDoughnutChart;
    gender: IDoughnutChart;
    totalHouseholdIncome: number;
    maritalStatus: IPieChart;
    totalRaces: IPieChart;
  } = {
    totalApplications: 0,
    statusSummary: {},
    totalCityEmployees: {
      dataLabels: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    gender: {
      dataLabels: ['Female', 'Male'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalHouseholdIncome: 0,
    maritalStatus: {
      label: ['Married', 'Unmarried', 'Widowed'],
      dataSets: [
        { data: 0, color: '#0FCA98' },
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#2B6CB0' },
      ],
    },
    totalRaces: {
      label: races,
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
        { data: 0, color: '#2B6CB0' },
        { data: 0, color: '#F27141' },
      ],
    },
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData } = a;

    if (a.status === 'APPROVED') {
      //Total Application Widget
      widgetsData.totalApplications += 1;

      //Status Summary
      widgetsData.statusSummary[a.status] = (widgetsData.statusSummary[a.status] ?? 0) + 1;

      if (applicationData?.application?.cityEmployee === 'yes') {
        widgetsData.totalCityEmployees.dataSets[0].data += 1;
      }
      if (applicationData?.application?.cityEmployee === 'no') {
        widgetsData.totalCityEmployees.dataSets[1].data += 1;
      }

      // gender
      if (applicationData?.application?.gender === 'Female') {
        widgetsData.gender.dataSets[0].data += 1;
      }
      if (applicationData?.application?.gender === 'Male') {
        widgetsData.gender.dataSets[1].data += 1;
      }

      // marital status
      if (applicationData?.application?.maritalStatus === 'Un-Married') {
        widgetsData.maritalStatus.dataSets[1].data += 1;
      }
      if (applicationData?.application?.maritalStatus === 'Married') {
        widgetsData.maritalStatus.dataSets[0].data += 1;
      }
      if (applicationData?.application?.maritalStatus === 'Widow') {
        widgetsData.maritalStatus.dataSets[2].data += 1;
      }

      // Total Household Income
      widgetsData.totalHouseholdIncome += calcTotalHouseholdIncome(applicationData);
    }
  });

  const ASSISTANCE_AMOUNT = 10_000;

  return (
    <div className='my-4'>
      <section className='my-4 grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
        <ApprovedApplicationsWidget count={widgetsData?.totalApplications ?? 0} />
        <TotalHousingPrice count={widgetsData.totalApplications * ASSISTANCE_AMOUNT} />
        <AverageHouseholdIncome count={getAverageHouseholdIncome(applicationsList, widgetsData)} />
      </section>

      <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
        <Card title='City Employees'>
          <div className='p-5 flex flex-col items-center'>
            <div className='w-5/6 lg:w-2/3'>
              <DoughnutChart
                dataLabels={widgetsData?.totalCityEmployees?.dataLabels}
                datasets={convertIntoValueAndPercentage(widgetsData?.totalCityEmployees?.dataSets)}
                showValueAndPercentage
                fontSize={14}
                padding={10}
                legendPosition='left'
                legendAlign='end'
              />
            </div>
          </div>
        </Card>
        <Card title='Gender'>
          <div className='p-5 flex flex-col items-center '>
            <div className='w-5/6 lg:w-2/3'>
              <DoughnutChart
                dataLabels={widgetsData?.gender?.dataLabels}
                datasets={convertIntoValueAndPercentage(widgetsData?.gender?.dataSets)}
                showValueAndPercentage
                fontSize={14}
                padding={10}
                legendPosition='left'
                legendAlign='end'
              />
            </div>
          </div>
        </Card>
      </section>
      <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
        <Card title='Marital Status'>
          <div className='p-5 flex flex-col items-center '>
            <div className='w-5/6 lg:w-2/3 h-80'>
              <PieChart
                labels={widgetsData?.maritalStatus?.label}
                datasets={convertIntoValueAndPercentage(widgetsData?.maritalStatus?.dataSets)}
                showValueAndPercentage
                chartTitle=''
              />
            </div>
          </div>
        </Card>
        <Card title='Race'>
          <div className='p-5 flex flex-col items-center '>
            <div className='w-5/6 lg:w-5/5 h-80'>
              <PieChart
                labels={widgetsData?.totalRaces?.label}
                datasets={convertIntoValueAndPercentage(widgetsData?.totalRaces?.dataSets)}
                showValueAndPercentage
                chartTitle=''
              />
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
};
